import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { AuthLayout } from "@/components/Layout";
import { AuthCard } from "@/components/Card";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { Alert } from "@/components/Alert";
import { Logo } from "@/components/Logo";
import { Turnstile } from "@/components/Turnstile";

// Get-Started page lists both pathways (WL + TRT) and links to their intakes.
const INTAKE_URL = "https://fortehealth.co/get-started";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});
type FormData = z.infer<typeof schema>;

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const prefillEmail = params.get("email") ?? "";
  const locState = loc.state as { from?: string; idleLogout?: boolean; archived?: boolean } | null;
  const from = locState?.from ?? "/dashboard";
  const [serverError, setServerError] = useState<string | null>(
    locState?.archived
      ? "Your account has been archived. Email care@fortehealth.co if this is unexpected."
      : locState?.idleLogout
      ? "You were signed out for inactivity. Sign back in to continue."
      : null,
  );
  const [captchaToken, setCaptchaToken] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: prefillEmail, password: "" },
  });

  async function onSubmit(values: FormData) {
    setServerError(null);
    if (!captchaToken) {
      setServerError("Please complete the verification challenge.");
      return;
    }
    const { error } = await supabase.auth.signInWithPassword({
      ...values,
      options: { captchaToken },
    });
    if (error) {
      // Supabase returns specific error strings for banned (archived) users.
      // Match conservatively — fall through to generic "invalid credentials"
      // for everything else to avoid leaking which emails exist.
      const msg = (error.message ?? "").toLowerCase();
      if (msg.includes("banned") || msg.includes("user is banned")) {
        setServerError("Your account has been archived. Email care@fortehealth.co if this is unexpected.");
      } else {
        setServerError("Invalid email or password.");
      }
      return;
    }
    nav(from, { replace: true });
  }

  return (
    <AuthLayout
      footnote="Your health information is private, encrypted, and never shared without your consent."
    >
      <AuthCard>
        <div className="mb-6 flex justify-center">
          <Logo size="lg" href="https://fortehealth.co" />
        </div>

        <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
          Welcome back
        </h1>
        <p className="text-[14px] text-fh-gray text-center mb-6">
          Sign in to manage your treatment and care.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3.5" noValidate>
          {serverError && <Alert tone="error">{serverError}</Alert>}

          <FormField
            label="Email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            autoFocus
            error={errors.email?.message}
            {...register("email")}
          />

          <FormField
            label="Password"
            type="password"
            autoComplete="current-password"
            placeholder="Enter your password"
            error={errors.password?.message}
            labelRight={
              <Link to="/forgot-password" className="text-[12px] font-semibold text-fh-green hover:text-fh-green-dark">
                Forgot?
              </Link>
            }
            {...register("password")}
          />

          <Turnstile siteKey={env.VITE_TURNSTILE_SITE_KEY} onToken={setCaptchaToken} />

          <Button type="submit" loading={isSubmitting} fullWidth className="mt-1">
            Sign in
          </Button>

          <p className="text-center text-[13px] text-fh-gray mt-1">
            <Link to="/forgot-password" className="font-semibold text-fh-green hover:text-fh-green-dark">
              Forgot your password?
            </Link>
          </p>
        </form>

        <div className="flex items-center gap-3 my-[18px]">
          <div className="flex-1 h-px bg-fh-border" />
        </div>

        <p className="text-center text-[13px] text-fh-gray">
          New patient?{" "}
          <a
            href={INTAKE_URL}
            className="font-semibold text-fh-green hover:text-fh-green-dark"
          >
            Start your assessment
          </a>
        </p>
      </AuthCard>
    </AuthLayout>
  );
}
